import { AxiosInstance } from 'axios'
import flow from 'lodash.flow'
import { inject } from 'tsyringe'
import VueI18n from 'vue-i18n'
import { httpToken } from '~/constants/dependency-injection/tokens'

import { containerScoped } from '~/decorators/dependency-container'
import {
  CategoriesSuggestion,
  CategoryGroup,
  CategoryHeader,
  CategoryPath,
  CategorySearchOptions
} from '~/models/category/types'
import { toCamelCase } from '~/utils/object'
import RequestBuilder from '~/builders/http/RequestBuilder'

const defaultSearchOptions: CategorySearchOptions = { headers: true }

export type CategoryId = Readonly<number | string>
type CategoryIdOrIds = CategoryId | Readonly<CategoryId[]>

@containerScoped()
export default class CategorySearchService {
  constructor(
    @inject(httpToken) private http: AxiosInstance,
    @inject(VueI18n) private i18n: VueI18n,
    @inject(RequestBuilder) private requestBuilder: RequestBuilder
  ) {}

  async fetchCategoryGroups(
    query: string,
    categoryIdOrIds: CategoryIdOrIds,
    siblingIdOrIds: CategoryId[],
    {
      headers = defaultSearchOptions.headers
    }: CategorySearchOptions = defaultSearchOptions
  ): Promise<Array<CategoryHeader | CategoriesSuggestion>> {
    return await this.requestBuilder
      .request('get', '/api/categories/for-classified-form/search/')
      .params({
        q: query,
        category: categoryIdOrIds,
        'sibling-category': siblingIdOrIds
      })
      .validate(body => body?.data)
      .map(body => this.normalizeData(toCamelCase(body.data.groups), headers))
      .send()
  }

  private getCategoryQueryParams(categoryIdOrIds: CategoryIdOrIds): string {
    return flow(
      (categoryIdOrIds: CategoryIdOrIds) =>
        Array.isArray(categoryIdOrIds) ? categoryIdOrIds : [categoryIdOrIds],
      categoryIds =>
        categoryIds.reduce<string>(
          (str, category) => str + `&category=${category}`,
          ''
        )
    )(categoryIdOrIds)
  }

  private pathToString(path: Array<CategoryPath>): string {
    return path.map(p => p.humanName).join(' » ')
  }

  private normalizeData(data: any[], headers: boolean): any[] {
    let rootCategory: number | null = null
    const normalizedData = data
      .map((t: CategoryGroup, i: number) => {
        const group = []
        if (t.suggestions.length) {
          headers &&
            group.push({
              header: t.label,
              rootCategory: t.rootCategory,
              id: `cgh${i}`,
              selectable: false
            } as CategoryHeader)
          group.push(...t.suggestions)
          return group
        } else if (headers) {
          group.push({
            header: t.label,
            rootCategory: t.rootCategory,
            id: `cgh${i}`,
            selectable: false
          } as CategoryHeader)
          group.push(this.getEmptyResultsSuggestion(i))
        }

        return group
      })
      .flat()

    !headers &&
      !normalizedData.length &&
      normalizedData.push(this.getEmptyResultsSuggestion())

    return normalizedData.map((s: CategoriesSuggestion | CategoryHeader) => {
      if (s.rootCategory) {
        rootCategory = s.rootCategory
      }
      let extraData = {}
      if (s?.category?.path) {
        extraData = {
          path: this.pathToString(s.category.path),
          id: s.category.id.toString()
        }
      }
      return {
        ...s,
        ...extraData,
        rootCategory
      }
    })
  }

  private getEmptyResultsSuggestion(
    idPostfix: number | string = ''
  ): CategoriesSuggestion {
    const i = idPostfix
    return {
      category: {
        humanName: this.i18n.t('no results found') as string,
        id: `cnoData${i}`,
        path: [],
        hasChildren: false
      },
      id: `noData${i}`,
      class: 'no-data',
      selectable: false
    }
  }
}
